import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useEffect, useMemo } from "react";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";

import api from "services/api";
import COMMON from "common";
import { formatDatePattern } from "common/calendar";
import { formatTimeValue } from "common/time";
import serveRequestErrors, { axiosCancelled } from "common/serve-request-errors";
import { getRecommendedReplacementLeaveByTime } from "common/overtime";
import AppStatus from "components/app-status";
import AppButton from "components/app-button";
import AppCloseIcon from "components/icons/app-close-icon";
import pdfIcon from "assets/images/pdf-icon.svg";
import rejectIcon from "assets/images/components/pages/leave-management/reject-icon.svg";
import checkedIcon from "assets/images/components/pages/leave-management/checked-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";

/* Which surface opened the modal - decides the visible fields and actions */
export const OVERTIME_MODAL_CONTEXT = {
	EMPLOYEE: "EMPLOYEE",
	MANAGER: "MANAGER",
};

export const AppOvertimeModal = (props, ref) => {
	const [data, setData] = useState({});
	const [visible, setVisible] = useState(false);
	const { context = OVERTIME_MODAL_CONTEXT.EMPLOYEE, approveRef, rejectReasonRef, cancelRef } = props;
	const isManager = useMemo(() => context === OVERTIME_MODAL_CONTEXT.MANAGER, [context]);

	const onHandleDismiss = useCallback(() => {
		setData({});
		setVisible(false);
	}, []);

	const onHandleShow = useCallback((id) => {
		setData((prev) => ({ ...prev, id }));
		setVisible(true);
	}, []);

	//prettier-ignore
	const onHandleApprove = useCallback(() => {
		approveRef?.current.onHandleShow(data);
		onHandleDismiss();
	}, [data, approveRef, onHandleDismiss]);

	//prettier-ignore
	const onHandleReject = useCallback(() => {
		rejectReasonRef?.current.onHandleShow(data.id);
		onHandleDismiss();
	}, [data.id, rejectReasonRef, onHandleDismiss]);

	//prettier-ignore
	const onHandleCancel = useCallback(() => {
		cancelRef?.current.onHandleShow(data.id);
		onHandleDismiss();
	}, [data.id, cancelRef, onHandleDismiss]);

	const overtimeInfo = useMemo(() => {
		const isRejected = data?.status === COMMON.STATUS_ID.REJECTED;

		const info = [
			{ label: "Employee Name", value: data.employeeName || "-" },
			{ label: "Overtime Date", value: data.overtimeDate ? formatDatePattern(data.overtimeDate) : "-" },
			{ label: "Start Time", value: formatTimeValue(data.startTime) || "-" },
			{ label: "End Time", value: formatTimeValue(data.endTime) || "-" },
			{ label: "Expiry Date", value: data.expiryLeaveDate ? formatDatePattern(data.expiryLeaveDate) : "-" },
			{ label: "Overtime Hours", value: data.overtimeHours ?? "-" },
			{ label: "Replacement Leave (Days)", value: data.replacementLeave ?? "-" },
		];

		/* The VO exposes allowanceAmount on the manager path too, so hide it here rather than server side */
		if (!isManager) {
			info.push({ label: "Allowance (RM)", value: data.allowanceAmount ?? "-" });
		}

		info.push({ label: "Submit Date", value: data.createdDate ? formatDatePattern(data.createdDate) : "-" });
		info.push({ label: "Attachment", value: data?.files?.[0] || "-" });
		info.push({ label: "Description", value: data.description || "-" });

		if (isRejected) {
			info.push({ label: "Reject Reason", value: data.rejectReason || "-" });
		}

		info.push({ label: "Remarks", value: data.remarks || "-" });

		return info;
	}, [data, isManager]);

	const recommendedReplacementLeave = useMemo(() => getRecommendedReplacementLeaveByTime(data.startTime, data.endTime), [data.startTime, data.endTime]);

	//prettier-ignore
	const actionButtons = useMemo(() => {
		if (isManager) {
			if (data.status !== COMMON.STATUS_ID.PENDING_MANAGER) return [];

			return [
				{ label: "Reject", outline: true, icon: rejectIcon, onClick: onHandleReject },
				{ label: "Approve", icon: checkedIcon, onClick: onHandleApprove },
			];
		}

		if (data.status !== COMMON.STATUS_ID.PENDING_MANAGER || !cancelRef) return [];

		return [{ label: "Cancel Overtime", outline: true, icon: removeIcon, onClick: onHandleCancel }];
	}, [isManager, data.status, cancelRef, onHandleReject, onHandleApprove, onHandleCancel]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	useEffect(() => {
		const onHandleGetDetails = async () => {
			let response = null;

			try {
				/* fileURL is a short lived presigned link, so always read it from a fresh fetch */
				if (isManager) response = await api.get.overtime.managerDetails(data.id);
				else response = await api.get.overtime.details(data.id);
			} catch (error) {
				// the record may be gone by the time it is opened, so close rather than
				// leave an empty modal behind the error toast
				if (!axiosCancelled(serveRequestErrors(error))) onHandleDismiss();
			}

			if (response) {
				setData((prev) => ({ ...prev, ...response }));
			}
		};

		if (data.id) onHandleGetDetails();
	}, [data.id, isManager, onHandleDismiss]);

	return (
		<Modal classes={{ root: "app-overtime-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="overtime-modal" aria-describedby="overtime-modal">
			<div className="overtime-modal">
				<div className="overtime-modal__main">
					<button className="overtime-modal__close-button" onClick={onHandleDismiss}>
						<AppCloseIcon color="#666666" />
					</button>

					<div className="overtime-modal__header">
						<p className="overtime-modal__title">Overtime Details</p>
					</div>

					<div className="info">
						<div className="info__box">
							<p className="overtime-modal__text">Recommended Replacement Leave</p>
							<p className="overtime-modal__title">{data.startTime ? recommendedReplacementLeave : "-"}</p>
						</div>
						<div className="info__box">
							<p className="overtime-modal__text">Status</p>
							<div className="overtime-modal__title">
								<AppStatus status={data?.status || "-"} description={data?.statusDescription} />
							</div>
						</div>
					</div>

					<div className="overtime-modal__body">
						<div className="overtime-modal__container">
							<div className="overtime-modal__wrapper">
								{overtimeInfo.map((o, i) => {
									const fileName = o.value?.fileName;

									return (
										<div className="overtime-modal__info" key={i}>
											<p className="overtime-modal__label">{o.label}</p>
											{!fileName && <p className="overtime-modal__value">{o.value}</p>}
											{fileName && (
												<p className="overtime-modal__value">
													<a href={o.value.fileURL} className="overtime-modal__link" target="_blank" rel="noopener noreferrer">
														<img src={pdfIcon} alt="attachment" /> {fileName}
													</a>
												</p>
											)}
										</div>
									);
								})}
							</div>

							<div className="overtime-modal__button-container">
								{actionButtons.map((btn, i) => (
									<AppButton key={i} type="button" outline={btn.outline} icon={btn.icon} label={btn.label} onClick={btn.onClick} />
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppOvertimeModal));

AppOvertimeModal.propTypes = {
	ref: PropTypes.object,
	context: PropTypes.oneOf(Object.values(OVERTIME_MODAL_CONTEXT)),
	approveRef: PropTypes.object,
	rejectReasonRef: PropTypes.object,
	cancelRef: PropTypes.object,
	onHandleGetList: PropTypes.func,
};
